import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { KeyRound, Pencil } from 'lucide-react'
import React from 'react'

import {
  CopyButton,
  createTranslator,
  type TranslateFn,
} from '@mangovoice/mango-ui'

import type { UserDetail } from '../types/user.types'

export interface UserGeneralInfoCardProps {
  user: UserDetail
  canEdit?: boolean
  onEdit?: () => void
  /** The Cognito key copied to the clipboard. The copy button renders only when set. */
  cognitoKey?: string | null
  /** Called after the Cognito-key copy succeeds — e.g. to toast confirmation. */
  onCognitoKeyCopied?: () => void
  /** Called with the error when the Cognito-key copy fails — e.g. to toast a failure. */
  onCognitoKeyCopyError?: (error: unknown) => void
  onResetPassword?: () => void
  isResetPasswordLoading?: boolean
  onToggleIsActive?: () => void
  isToggleIsActiveLoading?: boolean
  t?: TranslateFn
}

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

const InfoRow: React.FC<{
  label: string
  value: string
  isLink?: boolean
}> = ({ label, value, isLink = false }) => (
  <Box
    alignSelf="stretch"
    px={4}
    py={3}
    bg="bg.subtle"
    borderRadius="md"
    borderWidth="1px"
    borderColor="border.muted"
  >
    <HStack align="flex-start" justify="space-between" gap={4}>
      <Text fontSize="xs" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      <Text
        fontSize="xs"
        fontWeight="medium"
        color={isLink ? 'fg.info' : 'fg.muted'}
        textDecoration={isLink ? 'underline' : 'none'}
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  </Box>
)

export const UserGeneralInfoCard: React.FC<UserGeneralInfoCardProps> = ({
  user,
  canEdit = false,
  onEdit,
  cognitoKey,
  onCognitoKeyCopied,
  onCognitoKeyCopyError,
  onResetPassword,
  isResetPasswordLoading = false,
  onToggleIsActive,
  isToggleIsActiveLoading = false,
  t,
}) => {
  const tr = React.useMemo(() => createTranslator(t), [t])
  const fullName = `${user.first_name} ${user.last_name}`.trim()
  const email = user.email || '—'
  const toggleIsActiveLabel = user.is_active
    ? tr('users.generalInfo.deactivateUser', 'Deactivate User')
    : tr('users.generalInfo.activateUser', 'Activate User')
  const toggleIsActiveColorPalette = user.is_active ? 'error' : 'success'
  const copyCognitoKeyLabel = tr(
    'users.generalInfo.copyCognitoKey',
    'Copy "Cognito Key"',
  )

  return (
    <Card.Root boxShadow="sm" width="100%">
      <Card.Header p={4}>
        <HStack justify="space-between" align="center">
          <Heading as="h2" fontSize="lg" fontWeight="semibold">
            {tr('users.generalInfo.heading', 'User Information')}
          </Heading>
          <HStack gap={1}>
            {cognitoKey && (
              <CopyButton
                type="button"
                size="xs"
                variant="subtle"
                colorPalette="brand"
                value={cognitoKey}
                text={copyCognitoKeyLabel}
                copiedText={tr('users.generalInfo.cognitoKeyCopied', 'Copied')}
                onCopied={onCognitoKeyCopied}
                onCopyError={onCognitoKeyCopyError}
              />
            )}
            {onResetPassword && (
              <Button
                type="button"
                size="xs"
                variant="subtle"
                colorPalette="brand"
                aria-label={tr(
                  'users.generalInfo.resetPasswordAria',
                  'Reset password',
                )}
                onClick={onResetPassword}
                loading={isResetPasswordLoading}
              >
                <KeyRound size={14} />
                {tr('users.generalInfo.resetPassword', 'Reset Password')}
              </Button>
            )}
            {onToggleIsActive && (
              <Button
                type="button"
                size="xs"
                variant="subtle"
                colorPalette={toggleIsActiveColorPalette}
                aria-label={toggleIsActiveLabel}
                onClick={onToggleIsActive}
                loading={isToggleIsActiveLoading}
              >
                {toggleIsActiveLabel}
              </Button>
            )}
            {canEdit && onEdit && (
              <IconButton
                size="xs"
                variant="subtle"
                colorPalette="brand"
                aria-label={tr(
                  'users.generalInfo.editUserInformation',
                  'Edit user information',
                )}
                onClick={onEdit}
              >
                <Pencil size={14} />
              </IconButton>
            )}
          </HStack>
        </HStack>
      </Card.Header>
      <Card.Body p={4} pt={0}>
        <VStack align="stretch" gap={2}>
          <InfoRow
            label={tr('users.generalInfo.username', 'Username')}
            value={user.username || '—'}
          />
          <InfoRow
            label={tr('users.generalInfo.name', 'Name')}
            value={fullName || '—'}
          />
          <InfoRow
            label={tr('users.generalInfo.role', 'Role')}
            value={user.role || '—'}
          />
          <InfoRow
            label={tr('users.generalInfo.email', 'Email')}
            value={email}
            isLink={email !== '—'}
          />
          <InfoRow
            label={tr('users.generalInfo.lastLoggedIn', 'Last Logged In')}
            value={formatDateTime(user.last_login)}
          />
          <InfoRow
            label={tr('users.generalInfo.created', 'Created')}
            value={formatDateTime(user.created_at)}
          />
          <InfoRow
            label={tr('users.generalInfo.updated', 'Updated')}
            value={formatDateTime(user.updated_at)}
          />
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
